import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api, setAccessToken, setUnauthenticatedHandler } from '../api/client'
import type { AuthResult, UserProfile } from '../api/types'

interface AuthState {
  user: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  /** True when the signed-in user holds this permission. The server checks again on every call. */
  can: (permission: string) => boolean
  canAny: (...permissions: string[]) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const refreshTimer = useRef<number | undefined>(undefined)

  const clearSession = useCallback(() => {
    setAccessToken(null)
    setUser(null)
    window.clearTimeout(refreshTimer.current)
  }, [])

  /**
   * Refreshes shortly before the access token expires, so a user in the middle of typing a cash
   * row never sees a request fail and retry.
   */
  const scheduleRefresh = useCallback((expiresAt: string) => {
    window.clearTimeout(refreshTimer.current)

    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now()
    const delay = Math.max(msUntilExpiry - 60_000, 15_000)

    refreshTimer.current = window.setTimeout(async () => {
      try {
        const result = await api<AuthResult>('/auth/refresh', { method: 'POST', noRetry: true })
        setAccessToken(result.accessToken)
        setUser(result.user)
        scheduleRefresh(result.expiresAt)
      } catch {
        clearSession()
      }
    }, delay)
  }, [clearSession])

  const applySession = useCallback(
    (result: AuthResult) => {
      setAccessToken(result.accessToken)
      setUser(result.user)
      scheduleRefresh(result.expiresAt)
    },
    [scheduleRefresh],
  )

  // On boot the access token is gone (it lives in memory) but the refresh cookie may still be
  // valid, so a page reload should not force a new sign-in.
  useEffect(() => {
    setUnauthenticatedHandler(clearSession)

    let cancelled = false

    void (async () => {
      try {
        const result = await api<AuthResult>('/auth/refresh', { method: 'POST', noRetry: true })
        if (!cancelled) applySession(result)
      } catch {
        // No usable session. Expected on a first visit.
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      window.clearTimeout(refreshTimer.current)
    }
  }, [applySession, clearSession])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api<AuthResult>('/auth/login', {
        method: 'POST',
        body: { email, password },
        noRetry: true,
      })
      applySession(result)
    },
    [applySession],
  )

  const signOut = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST', noRetry: true })
    } finally {
      clearSession()
    }
  }, [clearSession])

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await api<void>('/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      })

      // The server revoked every session, including this one.
      clearSession()
    },
    [clearSession],
  )

  const value = useMemo<AuthState>(() => {
    const granted = new Set(user?.permissions ?? [])

    return {
      user,
      loading,
      signIn,
      signOut,
      changePassword,
      can: (permission) => granted.has(permission),
      canAny: (...permissions) => permissions.some((p) => granted.has(p)),
    }
  }, [user, loading, signIn, signOut, changePassword])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }
  return context
}
